import React, { useEffect, useState } from "react";

import type { CapturedPhoto } from "@/types/types.d";

import { cn } from "@/lib/styles.utils";
import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import { literals } from "@/constants/literals";
import { Icon } from "@/components/icons/Icon";
import AddPhotoButton from "./AddPhotoButton";
import { useCamera } from "../hooks/useCamera";

type Props = {
  photos?: CapturedPhoto[];
  onChange: (photos: CapturedPhoto[]) => void;
  maxPhotos?: number;
  enableFallback?: boolean;
};

export const CameraCapture: React.FC<Props> = ({
  photos = [],
  onChange,
  maxPhotos = 10,
}) => {
  const { videoRef, start, stop, takePhoto, ready, error } = useCamera();
  const [sessionCaptured, setSessionCaptured] = useState(false);
  const [cameraModal, setCameraModal] = useState(false);

  const openCamera = async () => {
    if (photos.length >= maxPhotos) return;
    setSessionCaptured(false);
    await start();
    setCameraModal(true);
  };

  const closeCamera = () => {
    stop();
    setCameraModal(false);
  };

  const handleCapture = async () => {
    if (!ready || sessionCaptured) return;
    const photo = await takePhoto();
    if (!photo) return;
    onChange([...photos, photo]);
    setSessionCaptured(true);
    stop();
    setCameraModal(false);
  };

  const remove = (id: string) => {
    onChange(photos.filter((photo) => photo.id !== id));
  };

  useEffect(() => {
    return () => {
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="w-full h-full">
      <div
        className={cn(
          !cameraModal ? "hidden" : "",
          "viewer fixed top-0 left-0 h-[100vh] w-[100vw] z-[500]",
        )}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ width: "100%", height: "100%", background: "#000" }}
        />
        <div className="absolute bottom-0 p-4 w-full">
          {error && (
            <p className="text-ib-pt-color-red-500">Error cámara: {error}</p>
          )}
          <div className="w-full mb-4">
            <PrimaryButton
              colour="red"
              size="medium"
              icon="addPicture"
              label={literals.takePhoto}
              onClick={handleCapture}
              disabled={!ready || sessionCaptured}
            />
          </div>
          <SecondaryButton
            colour="red"
            size="medium"
            label={literals.cancel}
            onClick={closeCamera}
          />
        </div>
      </div>

      <div className="w-full flex flex-row flex-wrap gap-[10px]">
        {photos.map((p) => (
          <div
            key={p.id}
            className="relative rounded-sm h-[calc((100vw-32px)/3)] w-[calc((100%-20px)/3)] md:w-[calc((100%-30px)/4)] md:h-[calc(((100vw-142px)/4)/3*2)] lg:w-[calc((100%-40px)/5)] lg:h-[calc(((100vw-152px)/5)/3*2)]"
          >
            <img
              src={p.url}
              alt={`Foto ${p.id}`}
              style={{
                height: "100%",
                aspectRatio: "1.5 / 1",
                objectFit: "cover",
                borderRadius: 6,
              }}
            />
            <button
              type="button"
              onClick={() => remove(p.id)}
              className="absolute top-0 right-0 size-8 bg-ib-pt-color-white rounded-sm"
            >
              <Icon name="trash" className="size-8 text-ib-pt-color-red-500" />
            </button>
          </div>
        ))}
        <AddPhotoButton
          openCamera={openCamera}
          disabled={photos.length === maxPhotos}
        />
      </div>
    </div>
  );
};
